import axios from 'axios'

const API_BASE = '/api/admin'

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Add user auth token to requests (admin now uses user authentication)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('user_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface AdminConfig {
  deepseek_model: string
  temperature: number
  max_tokens: number
  disable_thinking: boolean
  session_retention_days: number
  pubmed_rate_limit: string
  pubmed_batch_size: number
  pubmed_max_retries: number
  pubmed_retry_delay: number
  pubmed_lite_mode_default: boolean
  ai_batch_concurrency: number
  ai_batch_size: number
  ai_table_merge_mode: 'python' | 'llm'
  // Token weights
  cache_hit_weight: number
  cache_miss_weight: number
  output_weight: number
  // Pricing
  cache_hit_price?: number
  cache_miss_price?: number
  output_price?: number
  updated_at?: string
}

export interface AdminConfigUpdate {
  deepseek_api_key?: string
  ncbi_api_key?: string
  deepseek_model?: string
  temperature?: number
  max_tokens?: number
  disable_thinking?: boolean
  session_retention_days?: number
  pubmed_rate_limit?: string
  pubmed_batch_size?: number
  pubmed_max_retries?: number
  pubmed_retry_delay?: number
  pubmed_lite_mode_default?: boolean
  ai_batch_concurrency?: number
  ai_batch_size?: number
  ai_table_merge_mode?: 'python' | 'llm'
  // Token weights
  cache_hit_weight?: number
  cache_miss_weight?: number
  output_weight?: number
  // Pricing
  cache_hit_price?: number
  cache_miss_price?: number
  output_price?: number
}

export interface SessionInfo {
  session_id: string
  search_id: string | null
  created_at: string
  last_accessed: string
  expires_at: string
}

export async function getConfig(): Promise<AdminConfig> {
  const response = await api.get('/config')
  return response.data
}

export async function updateConfig(config: AdminConfigUpdate): Promise<AdminConfig> {
  const response = await api.put('/config', config)
  return response.data
}

export async function listSessions(limit = 100, offset = 0): Promise<{ sessions: SessionInfo[]; total: number }> {
  const response = await api.get('/sessions', { params: { limit, offset } })
  return response.data
}

export async function deleteSession(sessionId: string): Promise<{ message: string }> {
  const response = await api.delete(`/sessions/${sessionId}`)
  return response.data
}

export async function clearCache(pattern = '*'): Promise<{ message: string }> {
  const response = await api.post('/cache/clear', null, { params: { pattern } })
  return response.data
}

export interface ApiKeyStatus {
  deepseek_api_key_set: boolean
  ncbi_api_key_set: boolean
}

export async function getApiKeyStatus(): Promise<ApiKeyStatus> {
  const response = await api.get('/api-keys')
  return response.data
}

export async function updateApiKeys(data: { deepseek_api_key?: string; ncbi_api_key?: string }): Promise<ApiKeyStatus> {
  const response = await api.post('/api-keys', data)
  return response.data
}

export interface UserInfo {
  user_id: string
  email: string
  name: string | null
  plan: string
  is_active: boolean
  created_at: string
}

export async function listUsers(
  opts: { limit?: number; offset?: number; q?: string } = {},
): Promise<{ users: UserInfo[]; total: number }> {
  const { limit = 100, offset = 0, q } = opts
  const params: Record<string, any> = { limit, offset }
  if (q && q.trim()) params.q = q.trim()
  const response = await api.get('/users', { params })
  return response.data
}

export async function deleteUser(userId: string): Promise<{ message: string }> {
  const response = await api.delete(`/users/${userId}`)
  return response.data
}

// --- Plans ---

export interface PlanLimit {
  plan_name: string
  daily_limit: number
  monthly_limit: number
  created_at?: string
  updated_at?: string
}

export async function getPlanLimits(): Promise<PlanLimit[]> {
  const response = await api.get('/plans')
  return response.data
}

export async function updatePlanLimit(planName: string, limits: { daily_limit: number; monthly_limit: number }): Promise<PlanLimit> {
  const response = await api.put(`/plans/${planName}`, limits)
  return response.data
}

export async function updateUserPlan(userId: string, plan: string): Promise<{ message: string }> {
  const response = await api.put(`/users/${userId}/plan`, { plan })
  return response.data
}

// --- Metrics ---

export type MetricsRange = '7d' | '30d' | '90d' | 'all'

export interface MetricsTotals {
  users: number
  searches: number
  bertopic_analyses: number
  semantic_analyses: number
  llm_requests: number
  llm_tokens: number
}

export interface TokenCostTotals {
  cache_hit_tokens: number
  cache_miss_tokens: number
  output_tokens: number
  total_tokens: number
  estimated_cost: number | null
}

export interface MetricsDailyPoint {
  date: string
  searches: number
  bertopic: number
  semantic: number
  llm_requests: number
}

export interface MetricsOverview {
  range: string
  since: string | null
  totals: MetricsTotals
  token_costs: TokenCostTotals
  daily: MetricsDailyPoint[]
}

export interface UserMetrics {
  user_id: string
  email: string
  name: string | null
  search_count: number
  bertopic_count: number
  semantic_count: number
  ai_batch_count: number
  llm_requests: number
  llm_tokens: number
  cache_hit_tokens: number
  cache_miss_tokens: number
  output_tokens: number
  estimated_cost: number | null
  last_active: string | null
}

export interface RecentSearch {
  search_id: string
  user_id: string | null
  user_email: string | null
  query: string
  status: string
  total_count: number | null
  downloaded_count: number | null
  created_at: string
  completed_at: string | null
}

export async function getMetricsOverview(range: MetricsRange = '30d'): Promise<MetricsOverview> {
  const response = await api.get('/metrics/overview', { params: { range } })
  return response.data
}

export async function getUserMetrics(range: MetricsRange = '30d'): Promise<UserMetrics[]> {
  const response = await api.get('/metrics/users', { params: { range } })
  return response.data
}

export async function getRecentSearches(limit = 20): Promise<RecentSearch[]> {
  const response = await api.get('/metrics/recent-searches', { params: { limit } })
  return response.data
}

// --- Per-user detail ---

export interface UserDailyPoint {
  date: string
  searches: number
  bertopic: number
  semantic: number
  ai_batch: number
  llm_requests: number
  cache_hit_tokens: number
  cache_miss_tokens: number
  output_tokens: number
  estimated_cost: number | null
}

export interface UserUsageCaps {
  daily_limit: number
  monthly_limit: number
  used_daily: number
  used_monthly: number
}

export interface UserDetail {
  user_id: string
  email: string
  name: string | null
  plan: string
  is_active: boolean
  created_at: string | null
  last_active: string | null
  range: string
  since: string | null
  totals: UserMetrics
  daily: UserDailyPoint[]
  usage_caps: UserUsageCaps
}

export async function getUserDetail(userId: string, range: MetricsRange = '30d'): Promise<UserDetail> {
  const response = await api.get(`/users/${userId}/metrics`, { params: { range } })
  return response.data
}

// --- PubMed FTP Prefetch ---

export interface PrefetchJob {
  job_id: string
  year: number | null
  year_to: number | null
  files_count: number | null
  baseline_year: number
  status: 'pending' | 'running' | 'complete' | 'error' | 'cancelled'
  files_processed: number
  files_total_est: number | null
  articles_inserted: number
  articles_updated: number
  articles_skipped: number
  bytes_downloaded: number
  last_file_index: number
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

export async function startPrefetch(opts: {
  year?: number
  yearTo?: number
  filesCount?: number
  baselineYear?: number
}): Promise<PrefetchJob> {
  const response = await api.post('/prefetch', {
    year: opts.year,
    year_to: opts.yearTo,
    files_count: opts.filesCount,
    baseline_year: opts.baselineYear,
  })
  return response.data
}

export async function listPrefetchJobs(): Promise<PrefetchJob[]> {
  const response = await api.get('/prefetch')
  return response.data
}

export async function cancelPrefetch(jobId: string): Promise<{ message: string }> {
  const response = await api.delete(`/prefetch/${jobId}`)
  return response.data
}

export interface ArticleYearCount {
  year: number
  count: number
}

export async function getArticlesByYear(): Promise<ArticleYearCount[]> {
  const response = await api.get('/articles-by-year')
  return response.data
}

export interface BaselineStatus {
  baseline_year: number | null
  remote_total: number | null
  downloaded: number
  local_by_year: Record<string, number>
  error?: string
}

export async function getBaselineStatus(): Promise<BaselineStatus> {
  const response = await api.get('/prefetch/baseline-status')
  return response.data
}

// --- SJR (Scimago) re-annotation ---

export interface SjrReannotationStatus {
  status: 'idle' | 'running' | 'complete'
  processed: number
  updated: number
  total: number
}

export async function startSjrReannotation(): Promise<{ message: string }> {
  const response = await api.post('/articles/reannotate-sjr')
  return response.data
}

export async function getSjrReannotationStatus(): Promise<SjrReannotationStatus> {
  const response = await api.get('/articles/reannotate-sjr/status')
  return response.data
}
